import { api } from "./client";

/**
 * Project Transfer API Service
 *
 * Handles project transfer requests between users and organizations
 */

export interface ProjectTransfer {
  _id: string;
  projectId: {
    _id: string;
    name: string;
    description?: string;
    thumbnail?: string;
  };
  fromOwnerType: "user" | "organization";
  fromOwnerId: string;
  toOwnerType: "user" | "organization";
  toOwnerId: {
    _id: string;
    name: string;
    slug: string;
  };
  transferType: "user_to_org" | "org_to_org" | "org_to_user";
  status: "pending" | "approved" | "rejected" | "cancelled" | "expired";
  reason: string;
  notes?: string;
  requestedBy: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  requestedAt: string;
  approvedBy?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  approvedAt?: string;
  rejectedBy?: {
    _id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  rejectedAt?: string;
  rejectionReason?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  expiresAt: string;
  resourceSnapshot: Record<string, unknown>;
  dependentResources: {
    componentCount: number;
    workflowCount: number;
    analyticsCount: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface InitiateTransferData {
  toOrganizationId: string;
  reason: string;
  notes?: string;
}

export interface RejectTransferData {
  reason: string;
}

export const transfersApi = {
  /**
   * Initiate a project transfer from user to organization
   */
  async initiateTransfer(
    projectId: string,
    data: InitiateTransferData
  ): Promise<ProjectTransfer> {
    const response = await api.post<ProjectTransfer, InitiateTransferData>(
      `/projects/${projectId}/transfer`,
      data
    );
    if (!response.data) {
      throw new Error("Transfer response did not include transfer data");
    }
    return response.data;
  },

  /**
   * Get all transfer requests for the current user
   */
  async getUserTransfers(): Promise<ProjectTransfer[]> {
    const response = await api.get<ProjectTransfer[]>(
      "/projects/transfers"
    );
    return response.data || [];
  },

  /**
   * Get pending transfers for organizations where user is admin
   */
  async getPendingTransfers(): Promise<ProjectTransfer[]> {
    const response = await api.get<ProjectTransfer[]>(
      "/projects/transfers/pending"
    );
    return response.data || [];
  },

  /**
   * Approve a transfer request (organization admin only)
   */
  async approveTransfer(transferId: string): Promise<ProjectTransfer> {
    const response = await api.patch<ProjectTransfer>(
      `/projects/transfers/${transferId}/approve`,
      {} as Record<string, unknown>
    );
    if (!response.data) {
      throw new Error("Approve transfer response did not include transfer data");
    }
    return response.data;
  },

  /**
   * Reject a transfer request (organization admin only)
   */
  async rejectTransfer(
    transferId: string,
    data: RejectTransferData
  ): Promise<ProjectTransfer> {
    const response = await api.patch<ProjectTransfer, RejectTransferData>(
      `/projects/transfers/${transferId}/reject`,
      data
    );
    if (!response.data) {
      throw new Error("Reject transfer response did not include transfer data");
    }
    return response.data;
  },

  /**
   * Cancel a transfer request (requester only)
   */
  async cancelTransfer(transferId: string): Promise<void> {
    await api.delete(`/projects/transfers/${transferId}`);
  },
};
